import Editor, { OnMount, useMonaco } from "@monaco-editor/react";
import { ArrowLeft, Play, Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createScript, getScript, Script, updateScript } from "../api/scripts";
import { LaunchJobModal } from "../components/LaunchJobModal";

// URI synthétique : Monaco l'utilise pour matcher le JSON Schema via fileMatch.
const MODEL_URI = "inmemory://velvet-script.jsonc";

const TEMPLATE = `{
  // Nom court (slug-friendly)
  "name": "mon-script",

  "description": "Décris ici ce que fait ce script.",

  // Active jitter/pauses sur taps & typing
  "humanize": true,

  "steps": [
    { "action": "sleep", "duration_ms": 500 }
  ]
}
`;

export function ScriptEditorPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const monaco = useMonaco();
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [showLaunch, setShowLaunch] = useState(false);

  const [script, setScript] = useState<Script | null>(null);
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [content, setContent] = useState<string>(TEMPLATE);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // -------- Charge le script si edit --------
  useEffect(() => {
    if (isNew) return;
    let cancel = false;
    (async () => {
      try {
        const s = await getScript(id!);
        if (cancel) return;
        setScript(s);
        setName(s.name);
        setDescription(s.description ?? "");
        setContent(s.content);
        setDirty(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur de chargement");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [id, isNew]);

  // -------- Configure JSON Schema sur Monaco --------
  useEffect(() => {
    if (!monaco) return;
    const SCHEMA_URL = `${window.location.origin}/api/schemas/velvet-script.schema.json`;
    // `jsonDefaults` est exposé en runtime mais absent des types de @monaco-editor/react
    // → cast vers any pour accéder à l'API officielle monaco.languages.json.
    const jsonLang = (monaco.languages as unknown as {
      json: {
        jsonDefaults: {
          setDiagnosticsOptions: (opts: Record<string, unknown>) => void;
        };
      };
    }).json;
    jsonLang.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: true,
      trailingCommas: "warning",
      schemas: [
        {
          uri: SCHEMA_URL,
          fileMatch: [MODEL_URI],
        },
      ],
      enableSchemaRequest: true,
    });
  }, [monaco]);

  // -------- Save (Ctrl+S) --------
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        void handleSave();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, description, content, isNew, id]);

  async function handleSave() {
    if (!name.trim()) {
      setError("Le nom est requis");
      return;
    }
    if (!content.trim()) {
      setError("Le contenu est requis");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        const created = await createScript({
          name: name.trim(),
          description: description.trim() || undefined,
          content,
        });
        setDirty(false);
        navigate(`/scripts/${created.id}`, { replace: true });
      } else {
        const updated = await updateScript(id!, {
          name: name.trim(),
          description: description.trim() || null,
          content,
        });
        setScript(updated);
        setDirty(false);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  }

  const onMount: OnMount = (editor, monacoInstance) => {
    editorRef.current = editor;
    // Force le modèle à utiliser l'URI synthétique pour matcher le schéma
    const existing = editor.getModel();
    if (existing && existing.uri.toString() !== MODEL_URI) {
      const newModel = monacoInstance.editor.createModel(
        content,
        "json",
        monacoInstance.Uri.parse(MODEL_URI)
      );
      editor.setModel(newModel);
    }
  };

  if (loading) {
    return <div className="p-8 text-ink-500">Chargement…</div>;
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-ink-700 bg-ink-800/40 flex items-center gap-4">
        <button
          onClick={() => navigate("/scripts")}
          className="p-2 rounded-md text-ink-400 hover:bg-ink-700/50 hover:text-white transition-colors"
        >
          <ArrowLeft className="size-4" />
        </button>

        <div className="flex-1 grid grid-cols-3 gap-4">
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setDirty(true);
            }}
            placeholder="Nom du script"
            className="px-3 py-2 rounded-lg bg-ink-700/60 text-white text-sm border border-ink-600 focus:border-velvet-500 focus:outline-none"
          />
          <input
            type="text"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setDirty(true);
            }}
            placeholder="Description (optionnelle)"
            className="col-span-2 px-3 py-2 rounded-lg bg-ink-700/60 text-white text-sm border border-ink-600 focus:border-velvet-500 focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-2">
          {dirty && <span className="text-xs text-amber-400">● non sauvegardé</span>}
          <button
            onClick={() => void handleSave()}
            disabled={saving || !dirty}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 disabled:bg-ink-700 disabled:text-ink-500 text-white text-sm font-medium transition-colors"
          >
            <Save className="size-4" />
            {saving ? "…" : "Sauvegarder"}
          </button>
          {!isNew && script && (
            <button
              onClick={() => setShowLaunch(true)}
              disabled={dirty}
              title={dirty ? "Sauvegardez d'abord" : "Lancer un job"}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:bg-ink-700 disabled:text-ink-500 text-white text-sm font-medium transition-colors"
            >
              <Play className="size-4" />
              Lancer
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="px-6 py-2 bg-red-500/10 border-b border-red-500/30 text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="flex-1 min-h-0">
        <Editor
          height="100%"
          defaultLanguage="json"
          language="json"
          theme="vs-dark"
          value={content}
          onChange={(v) => {
            setContent(v ?? "");
            setDirty(true);
          }}
          onMount={onMount}
          options={{
            minimap: { enabled: true },
            scrollBeyondLastLine: false,
            fontSize: 13,
            fontFamily: "'JetBrains Mono', 'Fira Code', Menlo, monospace",
            tabSize: 2,
            insertSpaces: true,
            renderWhitespace: "selection",
            formatOnPaste: true,
            formatOnType: true,
          }}
        />
      </div>

      {showLaunch && script && (
        <LaunchJobModal
          script={script}
          onClose={() => setShowLaunch(false)}
          onLaunched={(job) => {
            setShowLaunch(false);
            navigate(`/jobs/${job.id}`);
          }}
        />
      )}
    </div>
  );
}
